import React, { useState, useEffect, useMemo } from "react";
import { apiClient } from "@/services/apiClient";
import { useAppStore } from "@/core/store/useAppStore";
import { NoteIcon } from "@/components/icons";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { PanelFilterTabs } from "@/components/ui/PanelFilterTabs";
import { PanelList } from "@/components/ui/PanelList";
import { PanelListItem } from "@/components/ui/PanelListItem";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { AddNotePopover } from "./AddNotePopover";
import { NoteDrawer } from "./NoteDrawer";
import {
  BaseNoteCard,
  ApproveButton,
  DiscardButton,
  toPlainPreview,
} from "@/components/NoteCard";
import { useVoiceClips, VoiceClipRow } from "@/core/voice";

interface Note {
  id: string;
  clientId: string;
  content: string;
  tags: string[];
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface NotesPanelProps {
  clientId: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export const NotesPanel: React.FC<NotesPanelProps> = ({
  clientId,
  collapsed,
  onToggleCollapse,
}) => {
  const { ui, triggerNotesRefresh } = useAppStore();
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  /** The note open in the drawer, by id, so a save or a refresh shows its newest copy. */
  const [openNoteId, setOpenNoteId] = useState<string | null>(null);
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const voiceTarget = useMemo(() => ({ client_id: clientId }), [clientId]);
  const voice = useVoiceClips({
    purpose: "client_note",
    target: voiceTarget,
    onCompleted: triggerNotesRefresh,
  });

  useEffect(() => {
    loadNotes();
  }, [clientId, ui.notesRefreshKey]);

  const loadNotes = async () => {
    try {
      setIsLoading(true);
      const fetchedNotes = await apiClient.get(`/client/${clientId}/notes`);
      setNotes(fetchedNotes);
    } catch (error) {
      console.error("Failed to load notes:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // The same calls as before. They reject on failure: the drawer stays where it was and says so.
  const handleUpdateNote = async (noteId: string, content: string, tags: string[]) => {
    const updatedNote = await apiClient.put<Note>(`/client/${clientId}/notes/${noteId}`, { content, tags });
    setNotes((current) => current.map((n) => (n.id === noteId ? updatedNote : n)));
  };

  const handleDeleteNote = async (noteId: string) => {
    await apiClient.delete(`/client/${clientId}/notes/${noteId}`);
    setNotes((current) => current.filter((n) => n.id !== noteId));
    // Only this note's own drawer closes: the user may have opened another while the call was slow.
    setOpenNoteId((current) => (current === noteId ? null : current));
  };

  const handleApprove = async (noteId: string) => {
    try {
      const updatedNote = await apiClient.post(
        `/client/${clientId}/notes/${noteId}/approve`,
      );
      setNotes(notes.map((n) => (n.id === noteId ? updatedNote : n)));
    } catch (error) {
      console.error("Failed to approve note:", error);
    }
  };

  const handleDiscard = async (noteId: string) => {
    try {
      await apiClient.post(`/client/${clientId}/notes/${noteId}/discard`);
      setNotes(notes.filter((n) => n.id !== noteId));
    } catch (error) {
      console.error("Failed to discard note:", error);
    }
  };

  const pendingNotes = notes.filter((n) => n.status === "pending");
  const approvedNotes = notes.filter((n) => n.status === "approved");

  const allTags = Array.from(
    new Set(approvedNotes.flatMap((n) => n.tags)),
  ).sort();

  const filteredNotes = filterTag
    ? approvedNotes.filter((n) => n.tags.includes(filterTag))
    : approvedNotes;

  // A note that leaves the list (deleted, or gone on a refresh) closes the drawer.
  const openNote = notes.find((n) => n.id === openNoteId) ?? null;

  return (
    <>
      <WorkspacePanel
        id="notes"
        title={ENTITY_CONFIG.note.plural}
        icon={<NoteIcon className="w-4 h-4" />}
        summary={
          pendingNotes.length > 0
            ? `${pendingNotes.length} pending`
            : `${approvedNotes.length} note${approvedNotes.length === 1 ? "" : "s"}`
        }
        loading={isLoading}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        className={collapsed ? "" : "h-full"}
        bodyClassName="min-h-[48rem] max-h-[48rem]"
        subheader={
          allTags.length > 0 ? (
            <PanelFilterTabs
              options={[
                { id: "all", label: "All", count: approvedNotes.length },
                ...allTags.map((tag) => ({
                  id: tag,
                  label: tag,
                  count: approvedNotes.filter((n) => n.tags.includes(tag)).length,
                })),
              ]}
              activeId={filterTag ?? "all"}
              onChange={(id) => setFilterTag(id === "all" ? null : id)}
            />
          ) : undefined
        }
        actions={<AddNotePopover clientId={clientId} variant="panel" onSendVoice={voice.send} />}
      >
        <div className="flex flex-col h-full">
      {voice.clips.length > 0 && (
        <div className="mb-3 rounded-lg border border-gray-100">
          {voice.clips.map((view) => (
            <VoiceClipRow
              key={view.key}
              view={view}
              onRetry={(clipId) => void voice.retry(clipId)}
              onDiscard={(clipId) => void voice.discard(clipId)}
              onRetryUpload={voice.retryUpload}
              onDiscardUpload={voice.discardUpload}
            />
          ))}
        </div>
      )}

      {pendingNotes.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-amber-700 mb-3">
            Pending Review ({pendingNotes.length})
          </h3>
          <div className="space-y-2">
            {pendingNotes.map((note) => (
              <BaseNoteCard
                key={note.id}
                note={{
                  id: note.id,
                  content: note.content,
                  tags: note.tags,
                  createdAt: note.createdAt,
                }}
                variant="pending"
                tagVariant="amber"
                showTimestamp={false}
                bottomLeft={
                  <div className="flex gap-2">
                    <ApproveButton onClick={() => handleApprove(note.id)} />
                    <DiscardButton onClick={() => handleDiscard(note.id)} />
                  </div>
                }
              />
            ))}
          </div>
        </div>
      )}

      <PanelList
        items={filteredNotes}
        keyFor={(n) => n.id}
        className="flex-1 h-auto"
        empty={
          <p className="text-sm text-gray-500 text-center py-4">
            {filterTag
              ? `No notes with tag "${filterTag}"`
              : "No notes yet"}
          </p>
        }
        renderItem={(note) => {
          const tagChips = note.tags.map((tag) => (
            <span
              key={tag}
              className="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-sm"
            >
              {tag}
            </span>
          ));
          return (
            <PanelListItem
              preview={toPlainPreview(note.content)}
              meta={tagChips.length > 0 ? tagChips : undefined}
              onPress={() => setOpenNoteId(note.id)}
            />
          );
        }}
      />

        </div>
      </WorkspacePanel>

      <NoteDrawer
        note={openNote}
        onClose={() => setOpenNoteId(null)}
        onSave={handleUpdateNote}
        onDelete={handleDeleteNote}
      />
    </>
  );
};
